import moment from "moment-timezone";
import { DateElement } from "../services/api/types/availability.type";
import { SessionDataType } from "../state/scheduler.store";
import { first, last } from "lodash-es";
import { extractDateFromISODateTime } from "./dateTime";
import i18n from "../i18n/i18n";

export interface UnavailableSlot {
  start: string;
  end: string;
}

interface TimeRange {
  rangeStart: string;
  rangeEnd: string;
}

export function getUnavailableSlots(
  input: DateElement[],
  slotDuration: number,
  startRange: string,
  endRange: string
): UnavailableSlot[] {
  const startDate = moment(startRange);
  const endDate = moment(endRange);
  const datesMap = new Map<string, DateElement>();

  // Map input dates for quick lookup
  input.forEach((day) =>
    datesMap.set(
      moment(extractDateFromISODateTime(day.date)).format("YYYY-MM-DD"),
      day
    )
  );

  const unavailableSlots: UnavailableSlot[] = [];

  // Generate all dates within the specified range
  for (
    let date = startDate.clone();
    date.isBefore(endDate);
    date.add(1, "day")
  ) {
    const dateStr = date.format("YYYY-MM-DD");
    const day = datesMap.get(dateStr);

    const startOfDay = date.clone().startOf("day");
    const endOfDay = startOfDay.clone().add(1440, "minutes");

    if (!day) {
      // If the date is not in the input, mark the whole day as unavailable
      unavailableSlots.push({
        start: startOfDay.format(),
        end: endOfDay.format()
      });
      continue;
    }

    // Sort the availability within the day
    day.availability.sort(
      (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute)
    );

    // Find gaps within the same day
    for (let j = 0; j < day.availability.length - 1; j++) {
      const currentSlot = day.availability[j];
      const nextSlot = day.availability[j + 1];

      const currentSlotEnd = startOfDay
        .clone()
        .hour(currentSlot.hour)
        .minute(currentSlot.minute)
        .add(slotDuration, "minutes");
      const nextSlotStart = startOfDay
        .clone()
        .hour(nextSlot.hour)
        .minute(nextSlot.minute);

      if (nextSlotStart.diff(currentSlotEnd, "minutes") > 0) {
        unavailableSlots.push({
          start: currentSlotEnd.format(),
          end: nextSlotStart.format()
        });
      }
    }

    // Find gap between start of the day and the first available slot
    if (day.availability.length > 0) {
      const firstSlot = day.availability[0];
      const firstSlotStart = startOfDay
        .clone()
        .hour(firstSlot.hour)
        .minute(firstSlot.minute);

      if (firstSlotStart.diff(startOfDay, "minutes") > 0) {
        unavailableSlots.push({
          start: startOfDay.format(),
          end: firstSlotStart.format()
        });
      }

      // Find gap between the last available slot and the end of the day
      const lastSlot = day.availability[day.availability.length - 1];
      const lastSlotEnd = startOfDay
        .clone()
        .hour(lastSlot.hour)
        .minute(lastSlot.minute)
        .add(slotDuration, "minutes");

      if (endOfDay.diff(lastSlotEnd, "minutes") > 0) {
        unavailableSlots.push({
          start: lastSlotEnd.format(),
          end: endOfDay.format()
        });
      }
    } else {
      // If no availability for the day, the whole day is unavailable
      unavailableSlots.push({
        start: startOfDay.format(),
        end: endOfDay.format()
      });
    }
  }

  // Sort the unavailable slots by start time
  unavailableSlots.sort(
    (a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()
  );

  return unavailableSlots;
}

export const getAllDatesFromRanges = (
  ranges: TimeRange[],
  timezone: string
): string[] => {
  const allDates: string[] = [];

  ranges.forEach((range) => {
    const rangeStart = moment.tz(range.rangeStart, timezone).startOf("day");
    const rangeEnd = moment
      .tz(range.rangeEnd, timezone)
      .subtract(1, "millisecond");

    const currentDate = rangeStart.clone();

    while (
      currentDate.isBefore(rangeEnd, "day") ||
      currentDate.isSame(rangeEnd, "day")
    ) {
      allDates.push(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }
  });

  return allDates;
};

export const findUnavailableRangesWithTimezones = (
  availableRanges: TimeRange[],
  inputTimezone: string,
  outputTimezone: string,
  startTime: string,
  endTime: string
): TimeRange[] => {
  if (!availableRanges.length) {
    return [
      {
        rangeStart: moment
          .tz(startTime, inputTimezone)
          .tz(outputTimezone)
          .toISOString(),
        rangeEnd: moment
          .tz(endTime, inputTimezone)
          .tz(outputTimezone)
          .toISOString()
      }
    ];
  }

  // Convert and sort ranges in input timezone
  const sortedRanges = availableRanges
    .map((range) => ({
      start: moment(range.rangeStart).tz(inputTimezone),
      end: moment(range.rangeEnd).tz(inputTimezone)
    }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const unavailableRanges: TimeRange[] = [];
  const start = moment(first(sortedRanges)?.start).startOf("week");
  const end = moment(last(sortedRanges)?.end);

  // Gap before first range
  if (sortedRanges[0].start.isSameOrAfter(start)) {
    unavailableRanges.push({
      rangeStart: start.tz("UTC").format(),
      rangeEnd: sortedRanges[0].start.tz("UTC").format()
    });
  }

  // Gaps between ranges
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    if (sortedRanges[i].end.isBefore(sortedRanges[i + 1].start)) {
      unavailableRanges.push({
        rangeStart: sortedRanges[i].end.tz(outputTimezone).format(),
        rangeEnd: sortedRanges[i + 1].start.tz(outputTimezone).format()
      });
    }
  }

  // Gap after last range
  if (sortedRanges.length > 0) {
    const lastRange = sortedRanges[sortedRanges.length - 1];
    if (lastRange.end.isBefore(end)) {
      unavailableRanges.push({
        rangeStart: lastRange.end.tz(outputTimezone).format(),
        rangeEnd: end.tz(outputTimezone).format()
      });
    }
  }

  return unavailableRanges;
};

export const getSlotCountMobiscrollDatePickerLabels = (
  computedAvailability: SessionDataType["computedAvailability"],
  t: typeof i18n.t
) => {
  return computedAvailability?.map((i) => ({
    date: i.date,
    color: i.availability.length === 0 ? "#ce4737" : "green",
    cssClass: i.availability.length > 0 ? "" : "custom-cell",
    cellCssClass: i.availability.length > 0 ? "" : "custom-cell",
    text: i.availability.length
      ? `${i.availability.length} ${i.availability.length === 1 ? t("ROOSTER.PUBLIC-APP.SLOTS", { defaultValue: "slots" }) : t("ROOSTER.PUBLIC-APP.SLOTS", { defaultValue: "slots" })}`
      : `0 ${t("ROOSTER.PUBLIC-APP.SLOTS", { defaultValue: "slots" })}`
  }));
};

export const isValidTimeSlot = (
  eventStart: Date,
  eventEnd: Date,
  invalidSlots: UnavailableSlot[]
): boolean => {
  return !invalidSlots.some((slot) => {
    const slotStart = new Date(slot.start);
    const slotEnd = new Date(slot.end);
    return (
      (eventStart >= slotStart && eventStart < slotEnd) ||
      (eventEnd > slotStart && eventEnd <= slotEnd) ||
      (eventStart <= slotStart && eventEnd >= slotEnd)
    );
  });
};
